import type { ComponentType } from "react";
import {
  Boxes,
  Building2,
  CalendarDays,
  DollarSign,
  MapPin,
  Package,
  Route,
  UserCog,
  Users,
} from "lucide-react";

export interface SidebarChildItem {
  to: string;
  label: string;
  exact?: boolean;
  matches?: (pathname: string) => boolean;
}

export interface SidebarItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  children?: SidebarChildItem[];
  /**
   * Start closed. Every group renders the same way — a row with a chevron —
   * so this decides the DEFAULT, not the shape. Configuration groups start
   * closed; the workspaces someone dispatches from start open.
   */
  defaultCollapsed?: boolean;
}

interface SidebarGroup {
  label: string;
  items: SidebarItem[];
}

type CarrierCapabilities =
  | {
      canManagePricing: boolean;
      canManageTeam: boolean;
      canManageCarrierSettings: boolean;
    }
  | null
  | undefined;

// Operations holds day-to-day carrier workflows. Settings holds carrier
// configuration, commercial setup, and access management.
const sidebarGroups: SidebarGroup[] = [
  {
    label: "Operations",
    items: [
      {
        to: "/fulfillment",
        label: "Fulfillment",
        icon: Package,
        children: [
          { to: "/fulfillment/orders", label: "Orders" },
          { to: "/fulfillment/shipments", label: "Shipments" },
          {
            to: "/fulfillment/approvals",
            label: "Approvals",
          },
        ],
      },
      {
        to: "/team",
        label: "Team",
        icon: UserCog,
        children: [
          { to: "/team", label: "Operators", exact: true },
          { to: "/team/vehicles", label: "Vehicles" },
        ],
      },
      { to: "/scheduling", label: "Scheduling", icon: CalendarDays },
      {
        to: "/trips",
        label: "Trips",
        icon: Route,
        children: [
          { to: "/trips", label: "Trips", exact: true },
          { to: "/trips/tasks", label: "Tasks" },
        ],
      },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/customers", label: "Customers", icon: Users },
      { to: "/service-areas", label: "Service areas", icon: MapPin },
      {
        to: "/pricing",
        label: "Pricing",
        icon: DollarSign,
        defaultCollapsed: true,
        children: [
          { to: "/pricing", label: "Rate Workspace", exact: true },
          {
            to: "/pricing/discounts",
            label: "Discounts",
          },
        ],
      },
      {
        to: "/services/shipping",
        label: "Services",
        icon: Boxes,
        defaultCollapsed: true,
        children: [
          {
            to: "/services/shipping",
            label: "Shipping",
          },
          {
            to: "/services/pickup",
            label: "Pickup",
            exact: true,
            matches: isPickupServiceRoute,
          },
          {
            to: "/services/insurance",
            label: "Insurance",
          },
          {
            to: "/services/packaging",
            label: "Packaging",
          },
          {
            to: "/services/signature",
            label: "Signature",
          },
          {
            to: "/services/special-handling",
            label: "Special handling",
          },
        ],
      },
      { to: "/settings/team", label: "Users & access", icon: Users },
      { to: "/settings/company", label: "Company", icon: Building2 },
      {
        to: "/settings/dispatch-centers",
        label: "Dispatch centers",
        icon: Building2,
      },
      { to: "/settings/delivery", label: "Delivery setup", icon: CalendarDays },
    ],
  },
];

export function getVisibleSidebarGroups(
  capabilities: CarrierCapabilities
): SidebarGroup[] {
  return sidebarGroups
    .map((group) => {
      const items = group.items
        .filter((item) => isSidebarItemVisible(item.to, capabilities))
        .map((item) => {
          if (!item.children) return item;
          return {
            ...item,
            children: item.children.filter((child) =>
              isSidebarItemVisible(child.to, capabilities)
            ),
          };
        })
        .filter((item) => !item.children || item.children.length > 0);

      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}

export function isSidebarRouteActive(
  pathname: string,
  item: Pick<SidebarChildItem, "to" | "exact" | "matches">
): boolean {
  if (item.matches) return item.matches(pathname);
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function isPickupServiceRoute(pathname: string): boolean {
  // Pickup is configured ON its list page now, so there is no descendant
  // path to match — and the sibling catalogues each own their own route.
  return pathname === "/services/pickup";
}

function isSidebarItemVisible(
  to: string,
  capabilities: CarrierCapabilities
): boolean {
  if (to === "/pricing") return !!capabilities?.canManagePricing;
  if (to.startsWith("/services") || to.startsWith("/services/pickup")) {
    return !!capabilities?.canManagePricing;
  }
  if (to === "/settings/team") return !!capabilities?.canManageTeam;
  if (
    to === "/settings/company" ||
    to === "/settings/dispatch-centers" ||
    to === "/settings/delivery"
  ) {
    return !!capabilities?.canManageCarrierSettings;
  }
  return true;
}
